import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import type { RuntimeConfig, ThemeId, WizardAnswers } from "@/types";
import { persistLocale } from "@/i18n";
import { applyTheme, readStoredTheme } from "@/theme";
import { defaultProducts } from "@/engine/questions";
import { resolveDocLink, resolveLocaleString } from "@/config/loadConfig";
import { trackEvent } from "@/analytics";

interface AppContextValue {
  config: RuntimeConfig;
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
  locale: string;
  setLocale: (locale: string) => void;
  answers: WizardAnswers;
  setAnswers: (answers: WizardAnswers) => void;
  resetAnswers: () => void;
  resolveLink: (refNumber: number, fallback?: string) => string;
  resolveString: (key: string, fallback?: string) => string;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  const { i18n } = useTranslation();
  const [theme, setThemeState] = useState<ThemeId>(() =>
    readStoredTheme(config.app.defaultTheme),
  );
  const [answers, setAnswersState] = useState<WizardAnswers>(() => ({
    products: defaultProducts(config.edition.wizard, i18n.language),
  }));

  const setTheme = useCallback((next: ThemeId) => {
    applyTheme(next);
    setThemeState(next);
    trackEvent("theme_changed", { theme: next });
  }, []);

  const setLocale = useCallback(
    async (next: string) => {
      persistLocale(next);
      await i18n.changeLanguage(next);
      setAnswersState({
        products: defaultProducts(config.edition.wizard, next),
      });
      trackEvent("locale_changed", { locale: next });
    },
    [config.edition.wizard, i18n],
  );

  const resetAnswers = useCallback(() => {
    setAnswersState({
      products: defaultProducts(config.edition.wizard, i18n.language),
    });
  }, [config.edition.wizard, i18n.language]);

  const resolveLink = useCallback(
    (refNumber: number, fallback = "") =>
      resolveDocLink(config.edition.docLinks, refNumber, i18n.language, fallback),
    [config.edition.docLinks, i18n.language],
  );

  const resolveString = useCallback(
    (key: string, fallback = "") =>
      resolveLocaleString(config.edition.localeStrings, key, i18n.language, fallback),
    [config.edition.localeStrings, i18n.language],
  );

  const value = useMemo(
    () => ({
      config,
      theme,
      setTheme,
      locale: i18n.language,
      setLocale,
      answers,
      setAnswers: setAnswersState,
      resetAnswers,
      resolveLink,
      resolveString,
    }),
    [config, theme, setTheme, i18n.language, setLocale, answers, resetAnswers, resolveLink, resolveString],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook export is required for the provider pattern.
// eslint-disable-next-line react-refresh/only-export-components
export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
